// frontend/src/pages/admin/AdminInventory.js
import React, { useState, useEffect, useCallback } from 'react';
import { inventoryApi } from '../../api/inventory';
import { storesApi } from '../../api/stores';
import { zonesApi } from '../../api/zones';
import { aiApi } from '../../api/ai';
import { settingsApi } from '../../api/settings';
import uploadService from '../../services/upload';
import { compressImage } from '../../utils/imageCompression';
import helpers from '../../utils/helpers';
import CameraModal from '../../components/common/CameraModal';
import { Link, useNavigate } from 'react-router-dom'; 
import { 
    FaSearch, FaPlus, FaEdit, FaTrash, FaTimes, FaSpinner, 
    FaStore, FaWeightHanging, FaCamera, FaFolderOpen, FaCheck, FaBan, FaEye, FaMagic, FaArrowLeft, FaShippingFast,
    FaBoxOpen, FaFilter, FaTags, FaCalculator 
} from 'react-icons/fa';

// --- CONFIGURATION CONSTANT ---
const EXCLUSIVE_STORE_ID = "everything_automotive_exclusive";
// ------------------------------

// Prefilled data structures
const PART_CATEGORIES = [
    { id: 'engine_components', label: 'Engine Components' },
    { id: 'brake_system', label: 'Brake System' },
    { id: 'suspension_steering', label: 'Suspension & Steering' },
    { id: 'transmission_drivetrain', label: 'Transmission & Drivetrain' },
    { id: 'electrical_system', label: 'Electrical System' },
    { id: 'cooling_heating', label: 'Cooling & Heating' },
    { id: 'body_exterior', label: 'Body & Exterior' },
    { id: 'interior_components', label: 'Interior Components' },
    { id: 'tires_wheels', label: 'Tires & Wheels' },
    { id: 'fluids_chemicals', label: 'Fluids & Chemicals' },
];

const PART_BRANDS = [
    { id: 'toyota_genuine', label: 'Toyota' },
    { id: 'honda_oem', label: 'Honda' },
    { id: 'denso', label: 'Denso' },
    { id: 'bosch', label: 'Bosch' },
    { id: 'rana_motors', label: 'Rana Motors' },
    { id: 'union_autoparts', label: 'Union Autoparts' },
    { id: 'mobil_1', label: 'Mobil 1' },
];

const CONDITIONS = [
    'New (In Box)', 
    'New (Open Box)', 
    'Remanufactured', 
    'Used (Tested)', 
    'Used (Untested)'
];

const IMAGE_SLOTS = [
    { key: 'front_jpg', label: 'Primary Product View *' },
    { key: 'label_jpg', label: 'Box / Label View (Part #)' },
    { key: 'connector_jpg', label: 'Connector / Mounting View' },
    { key: 'stamp_jpg', label: 'Part Number Stamp (Engraving)' }
];

const DELIVERY_OPTIONS = [
    { value: '', label: 'Not Specified (Default)' },
    { value: '2-3 Days', label: '2-3 Days (Local)' },
    { value: '3-5 Days', label: '3-5 Days (Regional)' },
    { value: '5-7 Days', label: '5-7 Days (National)' },
    { value: '7-10 Days', label: '7-10 Days (Special Order)' },
    { value: '10-14 Days', label: '10-14 Days (Special Order)' },
    { value: '14-21 Days', label: '14-21 Days (Import/Bulk)' },
    { value: '21-30 Days', label: '21-30 Days (Import/Bulk)' },
];

const CATEGORY_GROUPS = {
    "Engine Components": [
        { id: 'engine_components', label: 'All Engine Parts' },
        { id: 'air_filters', label: 'Air Filters' },
        { id: 'oil_filters', label: 'Oil Filters' },
        { id: 'spark_plugs', label: 'Spark Plugs' },
        { id: 'ignition_coils', label: 'Ignition Coils' },
        { id: 'belts_hoses', label: 'Belts & Hoses' },
        { id: 'gaskets_seals', label: 'Gaskets & Seals' },
        { id: 'pistons_rings', label: 'Pistons & Rings' }
    ],
    "Brake System": [
        { id: 'brake_system', label: 'All Brake Parts' },
        { id: 'brake_pads', label: 'Brake Pads' },
        { id: 'brake_rotors', label: 'Brake Rotors/Discs' },
        { id: 'brake_calipers', label: 'Brake Calipers' },
        { id: 'brake_fluid', label: 'Brake Fluid' },
        { id: 'brake_shoes', label: 'Brake Shoes' }
    ],
    "Suspension & Steering": [
        { id: 'suspension_steering', label: 'All Suspension' },
        { id: 'shock_absorbers', label: 'Shock Absorbers' },
        { id: 'control_arms', label: 'Control Arms' },
        { id: 'ball_joints', label: 'Ball Joints' },
        { id: 'tie_rod_ends', label: 'Tie Rod Ends' },
        { id: 'steering_racks', label: 'Steering Racks' }
    ],
    "Transmission": [
        { id: 'transmission_drivetrain', label: 'All Transmission' },
        { id: 'clutch_kits', label: 'Clutch Kits' },
        { id: 'cv_joints_axles', label: 'CV Joints & Axles' },
        { id: 'transmission_fluid', label: 'Transmission Fluid' }
    ],
    "Electrical": [
        { id: 'electrical_system', label: 'All Electrical' },
        { id: 'batteries', label: 'Batteries' },
        { id: 'alternators', label: 'Alternators' },
        { id: 'starters', label: 'Starters' },
        { id: 'sensors', label: 'Sensors' },
        { id: 'fuses_relays', label: 'Fuses & Relays' }
    ],
    "Body & Exterior": [
        { id: 'body_exterior', label: 'All Body Parts' },
        { id: 'headlights', label: 'Headlights' },
        { id: 'tail_lights', label: 'Tail Lights' },
        { id: 'bumpers', label: 'Bumpers' },
        { id: 'mirrors', label: 'Mirrors' },
        { id: 'wipers', label: 'Wiper Blades' }
    ],
    "Tires & Wheels": [
        { id: 'tires_wheels', label: 'All Tires/Wheels' },
        { id: 'tires', label: 'Tires' },
        { id: 'wheels_rims', label: 'Wheels & Rims' },
        { id: 'wheel_bearings', label: 'Wheel Bearings' }
    ],
    "Fluids & Chemicals": [
        { id: 'fluids_chemicals', label: 'All Fluids' },
        { id: 'engine_oil', label: 'Engine Oil' },
        { id: 'coolant', label: 'Coolant' }
    ],
    "Other Systems": [
        { id: 'cooling_heating', label: 'Cooling & Heating' },
        { id: 'exhaust_system', label: 'Exhaust System' },
        { id: 'fuel_system', label: 'Fuel System' },
        { id: 'interior_components', label: 'Interior' },
        { id: 'tools_equipment', label: 'Tools' }
    ]
};

const initialFormState = {
    itemname: '',
    part_number: '',
    price: '',
    discount_percentage: 0, // <--- NEW FIELD
    make: '',
    model: '',
    year_range: '',
    stock_quantity: 0,
    description: '',
    category_id: PART_CATEGORIES[0].id,
    brand_id: PART_BRANDS[0].id,
    store_id: EXCLUSIVE_STORE_ID, 
    images: {}, 
    pay_on_delivery_status: 'Not Specified',
    refundable_status: 'Not Specified',
    weight_kg: '',
    is_oem: false,
    condition: 'New (In Box)',
    expected_delivery_date: '',
    // Custom Fields
    custom_category: '',
    custom_brand: '',
    custom_condition: ''
};

function AdminInventory() {
    const navigate = useNavigate();
    const [parts, setParts] = useState([]);
    const [stores, setStores] = useState([]);
    const [filteredParts, setFilteredParts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [mediaConfig, setMediaConfig] = useState({});
    
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [updatingId, setUpdatingId] = useState(null); 

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPart, setEditingPart] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    const [isCustomCategory, setIsCustomCategory] = useState(false);
    const [isCustomBrand, setIsCustomBrand] = useState(false);
    const [isCustomCondition, setIsCustomCondition] = useState(false);

    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [activeViewType, setActiveViewType] = useState(null);
    const [uploadingSlot, setUploadingSlot] = useState(null);
    
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setFilteredParts(
            parts.filter(p => 
                (filterStatus === 'all' || p.status === filterStatus) && 
                (p.itemname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (p.part_number && p.part_number.toLowerCase().includes(searchTerm.toLowerCase())))
            )
        );
        setSelectedIds([]);
    }, [searchTerm, parts, filterStatus]); 

    const fetchData = async () => {
        try {
            setLoading(true);
            const [partsData, storesData, settingsData] = await Promise.all([
                inventoryApi.getAllPartsAdmin(),
                storesApi.getAllStores().catch(err => {
                    console.warn("Could not fetch stores:", err);
                    return [];
                }),
                settingsApi.getAllSettings()
            ]);
            setParts(partsData);
            setStores(storesData);
            setMediaConfig(settingsData.media_config || {});
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };


    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = filteredParts.map(p => p.id);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id) => {
        setSelectedIds(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} items?`)) return;

        setIsBulkDeleting(true);
        try {
            await Promise.all(selectedIds.map(id => inventoryApi.deletePart(id)));
            alert("Selected items deleted successfully.");
            setSelectedIds([]);
            fetchData();
        } catch (err) {
            alert("Some items could not be deleted.");
            fetchData();
        } finally {
            setIsBulkDeleting(false);
        }
    };

    const handleOpenModal = (part = null) => {
        if (part) {
            setEditingPart(part);
            
            const isCatCustom = part.custom_category ? true : false;
            const isBrandCustom = part.custom_brand ? true : false;
            const isCondCustom = !CONDITIONS.includes(part.condition);

            setIsCustomCategory(isCatCustom);
            setIsCustomBrand(isBrandCustom);
            setIsCustomCondition(isCondCustom);

            setFormData({
                ...initialFormState,
                ...part,
                price: part.price || '',
                discount_percentage: part.discount_percentage || 0, // <--- LOAD DISCOUNT
                stock_quantity: part.stock_quantity || 0,
                store_id: part.store_id || EXCLUSIVE_STORE_ID, 
                weight_kg: part.weight_kg || '',
                is_oem: part.is_oem || false,
                
                category_id: isCatCustom ? '' : (part.category_id || PART_CATEGORIES[0].id),
                custom_category: part.custom_category || '',
                
                brand_id: isBrandCustom ? '' : (part.brand_id || PART_BRANDS[0].id),
                custom_brand: part.custom_brand || '',

                condition: isCondCustom ? '' : (part.condition || CONDITIONS[0]),
                custom_condition: isCondCustom ? part.condition : '',
                
                expected_delivery_date: part.expected_delivery_date || ''
            });
        } else {
            setEditingPart(null);
            setFormData(initialFormState);
            setIsCustomCategory(false);
            setIsCustomBrand(false);
            setIsCustomCondition(false);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPart(null);
        setFormData(initialFormState);
        setIsCustomCategory(false);
        setIsCustomBrand(false);
        setIsCustomCondition(false);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'category_id') {
            if (value === 'other') {
                setIsCustomCategory(true);
                setFormData(prev => ({ ...prev, category_id: '', custom_category: '' }));
            } else {
                setIsCustomCategory(false);
                setFormData(prev => ({ ...prev, category_id: value, custom_category: '' }));
            }
        } 
        else if (name === 'brand_id') {
            if (value === 'other') {
                setIsCustomBrand(true);
                setFormData(prev => ({ ...prev, brand_id: '', custom_brand: '' }));
            } else {
                setIsCustomBrand(false);
                setFormData(prev => ({ ...prev, brand_id: value, custom_brand: '' }));
            }
        }
        else if (name === 'condition') {
            if (value === 'other') {
                setIsCustomCondition(true);
                setFormData(prev => ({ ...prev, condition: '', custom_condition: '' }));
            } else {
                setIsCustomCondition(false);
                setFormData(prev => ({ ...prev, condition: value, custom_condition: '' }));
            }
        }
        else {
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    const handleGenerateDescription = async () => {
        if (!formData.itemname || !formData.make || !formData.model) {
            alert("Please fill in Item Name, Make, and Model first.");
            return;
        }

        setIsGenerating(true);
        try {
            const selectedStore = stores.find(s => s.id === formData.store_id);
            const storeName = selectedStore ? selectedStore.store_name : null;
            
            const finalCondition = isCustomCondition ? formData.custom_condition : formData.condition;

            const payload = {
                item_type: 'part',
                name: formData.itemname,
                make: formData.make,
                model: formData.model,
                condition: finalCondition || 'New',
                features: formData.part_number,
                store_name: storeName
            };
            
            const response = await aiApi.generateDescription(payload);
            
            if (response.description) {
                setFormData(prev => ({ ...prev, description: response.description }));
            } else {
                alert("AI failed to generate a description.");
            }
        } catch (err) {
            alert("AI Generation failed. Check API key or network.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCameraClick = (viewType) => {
        setActiveViewType(viewType);
        setIsCameraOpen(true);
    };

    const handleCameraCapture = async (file) => {
        await processFile(file, activeViewType);
        setIsCameraOpen(false);
    };

    const processFile = async (file, viewType) => {
        if (!file) return;
        setUploadingSlot(viewType);
        setUploading(true);
        try {
            const compressionQuality = mediaConfig.parts_compression || 0.6;
            const compressed = await compressImage(file, compressionQuality);
            const response = await uploadService.uploadImage(compressed, 'part-images');
            
            setFormData(prev => ({
                ...prev,
                images: { ...prev.images, [viewType]: response.url }
            }));
        } catch (err) {
            alert("Image upload failed.");
        } finally {
            setUploading(false);
            setUploadingSlot(null);
        }
    };

    const handleFileInput = (e, viewType) => {
        processFile(e.target.files[0], viewType);
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...formData,
                price: parseFloat(formData.price),
                discount_percentage: parseFloat(formData.discount_percentage) || 0, // <--- SEND DISCOUNT
                stock_quantity: parseInt(formData.stock_quantity),
                weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
                store_id: formData.store_id || null, 
                expected_delivery_date: formData.expected_delivery_date || null
            };

            if (isCustomCategory) {
                payload.category_id = null;
            } else {
                payload.custom_category = null;
            }

            if (isCustomBrand) {
                payload.brand_id = null;
            } else {
                payload.custom_brand = null;
            }

            if (isCustomCondition) {
                payload.condition = formData.custom_condition;
            }
            delete payload.custom_condition;
            
            if (editingPart) {
                await inventoryApi.updatePart(editingPart.id, payload);
            } else {
                await inventoryApi.createPart(payload);
            }
            
            fetchData();
            handleCloseModal();
        } catch (err) {
            alert(`Failed to save part: ${err.message || err.data?.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (partId) => {
        if (window.confirm("Are you sure you want to delete this part permanently?")) {
            setUpdatingId(partId);
            try {
                await inventoryApi.deletePart(partId);
                setParts(prev => prev.filter(p => p.id !== partId));
                setSelectedIds(prev => prev.filter(id => id !== partId));
            } catch (err) {
                alert("Failed to delete part.");
            } finally {
                setUpdatingId(null);
            }
        }
    };

    const handleStatusUpdate = async (partId, status) => {
        if (!window.confirm(`Mark this part as ${status.toUpperCase()}?`)) return;
        setUpdatingId(partId);
        try {
            await inventoryApi.updatePartStatus(partId, status);
            setParts(prev => prev.map(p => p.id === partId ? { ...p, status } : p));
        } catch (err) {
            alert("Failed to update status.");
        } finally {
            setUpdatingId(null);
        }
    };

    const getStoreName = (id) => {
        if (id === EXCLUSIVE_STORE_ID) return "Everything Automotive Exclusive Store"; 
        const store = stores.find(s => s.id === id);
        return store ? store.store_name : 'General Inventory';
    };

    const getStatusBadge = (status) => {
        const s = status || 'active'; 
        if (s === 'active') return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded font-bold">ACTIVE</span>;
        if (s === 'pending_review') return <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded font-bold">PENDING</span>;
        if (s === 'rejected') return <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded font-bold">REJECTED</span>;
        return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">{s}</span>;
    };

    // Helper to calculate final price for display in modal
    const calculateFinalPrice = () => {
        const price = parseFloat(formData.price) || 0;
        const discount = parseFloat(formData.discount_percentage) || 0;
        if (discount <= 0) return price;
        return price - (price * (discount / 100));
    };

    if (loading) return <div className="p-8 text-center">Loading inventory...</div>;

    return (
        <div>
            <div className="mb-4">
                <button 
                    onClick={() => navigate('/admin/ea-hub')} 
                    className="text-gray-500 hover:text-primary flex items-center gap-2 text-sm font-bold"
                >
                    <FaArrowLeft /> Back to EA Hub
                </button>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <FaBoxOpen className="text-primary" /> Parts Inventory
                </h1>
                
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <button 
                        onClick={() => handleOpenModal()} 
                        className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                        <FaPlus /> Add New Part
                    </button>
                </div>
            </div>


            {/* FILTERS & BULK ACTIONS */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2 w-full md:w-auto">
                    <FaFilter className="text-gray-400" />
                    <select 
                        value={filterStatus} 
                        onChange={(e) => setFilterStatus(e.target.value)} 
                        className="border border-gray-300 rounded px-3 py-2"
                    >
                        <option value="all">All Statuses</option>
                        <option value="pending_review">Pending Review</option>
                        <option value="active">Active</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>

                <div className="relative w-full md:w-64">
                    <input type="text" placeholder="Search by Name or Part #" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded"/>
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>

                {selectedIds.length > 0 && (
                    <button 
                        onClick={handleBulkDelete}
                        disabled={isBulkDeleting}
                        className="bg-red-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2 hover:bg-red-700 transition-colors shadow-sm w-full md:w-auto justify-center"
                    >
                        {isBulkDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />} 
                        Delete Selected ({selectedIds.length})
                    </button>
                )}
            </div>

            {/* TABLE */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[800px]">
                        <thead>
                            <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider border-b border-gray-200">
                                <th className="p-4 w-10">
                                    <input 
                                        type="checkbox" 
                                        onChange={handleSelectAll}
                                        checked={filteredParts.length > 0 && selectedIds.length === filteredParts.length}
                                        className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                                    />
                                </th>
                                <th className="p-4">Part Name</th>
                                <th className="p-4">Store</th>
                                <th className="p-4">Price</th>
                                <th className="p-4">Status</th>
                                <th className="p-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredParts.map(part => (
                                <tr key={part.id} className={`hover:bg-gray-50 ${selectedIds.includes(part.id) ? 'bg-blue-50' : ''}`}>
                                    <td className="p-4">
                                        <input 
                                            type="checkbox" 
                                            checked={selectedIds.includes(part.id)}
                                            onChange={() => handleSelectOne(part.id)}
                                            className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                                        />
                                    </td>
                                    <td className="p-4">
                                        <div className="font-bold text-gray-800">{part.itemname}</div>
                                        <div className="text-xs text-gray-500">{part.part_number}</div>
                                        {part.is_oem && <span className="text-[10px] bg-blue-600 text-white px-1 rounded">OEM</span>}
                                        {part.discount_percentage > 0 && (
                                            <span className="text-[10px] bg-red-100 text-red-600 px-1 rounded font-bold ml-1">-{part.discount_percentage}%</span>
                                        )}
                                    </td>
                                    <td className="p-4 text-sm text-gray-600">
                                        <span className={`px-2 py-1 rounded text-xs ${part.store_id === EXCLUSIVE_STORE_ID ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                                            {getStoreName(part.store_id)}
                                        </span>
                                    </td>
                                    <td className="p-4 text-sm font-bold text-primary">{helpers.formatCurrency(part.price, 'NGN')}</td>
                                    <td className="p-4">
                                        {getStatusBadge(part.status)}
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <Link 
                                                to={`/parts/${part.id}`} 
                                                target="_blank" 
                                                className="p-2 text-gray-600 hover:bg-gray-200 rounded" 
                                                title="View Public Page"
                                            >
                                                <FaEye />
                                            </Link>

                                            {part.status === 'pending_review' && (
                                                <>
                                                    <button onClick={() => handleStatusUpdate(part.id, 'active')} disabled={updatingId === part.id} className="p-2 text-green-600 hover:bg-green-100 rounded" title="Approve"><FaCheck /></button>
                                                    <button onClick={() => handleStatusUpdate(part.id, 'rejected')} disabled={updatingId === part.id} className="p-2 text-red-600 hover:bg-red-100 rounded" title="Reject"><FaBan /></button>
                                                </>
                                            )}
                                            <button onClick={() => handleOpenModal(part)} className="p-2 text-blue-600 hover:bg-blue-100 rounded"><FaEdit /></button>
                                            <button onClick={() => handleDelete(part.id)} disabled={updatingId === part.id} className="p-2 text-red-600 hover:bg-red-100 rounded" title="Delete Permanently">
                                                {updatingId === part.id ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- ADD/EDIT MODAL --- */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-fadeInUp">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h3 className="text-xl font-bold">{editingPart ? 'Edit Part' : 'Create New Part'}</h3>
                            <button onClick={handleCloseModal}><FaTimes /></button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4">
                            
                            {/* Store Selection */}
                            <div className="bg-blue-50 p-4 rounded border border-blue-100 mb-4">
                                <label className="block text-sm font-bold text-blue-800 mb-1 flex items-center gap-2">
                                    <FaStore /> Assign to Store *
                                </label>
                                <select 
                                    name="store_id" 
                                    value={formData.store_id} 
                                    onChange={handleInputChange} 
                                    required
                                    className="p-2 border rounded w-full bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">-- Select Store --</option>
                                    <optgroup label="Internal / EA Branches">
                                        <option value={EXCLUSIVE_STORE_ID}>Everything Automotive (System Default)</option>
                                        {stores.filter(s => s.id !== EXCLUSIVE_STORE_ID && s.is_internal).map(store => (
                                            <option key={store.id} value={store.id}>{store.store_name}</option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="Partner Stores">
                                        {stores.filter(s => !s.is_internal).map(store => (
                                            <option key={store.id} value={store.id}>{store.store_name}</option>
                                        ))}
                                    </optgroup>
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Item Name *</label>
                                    <input name="itemname" value={formData.itemname} onChange={handleInputChange} required className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Part Number (Optional)</label>
                                    <input name="part_number" value={formData.part_number} onChange={handleInputChange} placeholder="e.g. 04465-0K090" className="p-2 border rounded w-full" />
                                </div>
                            </div>

                            {/* --- PRICING & DISCOUNT SECTION --- */}
                            <div className="bg-blue-50 p-4 rounded border border-blue-100">
                                <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2"><FaTags /> Pricing & Discounts</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Original Price (NGN) *</label>
                                        <input type="number" name="price" value={formData.price} onChange={handleInputChange} required className="w-full p-2 border rounded" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Discount (%)</label>
                                        <input 
                                            type="number" 
                                            name="discount_percentage" 
                                            value={formData.discount_percentage} 
                                            onChange={handleInputChange} 
                                            min="0" 
                                            max="100"
                                            className="w-full p-2 border rounded" 
                                            placeholder="0"
                                        />
                                    </div>
                                    <div className="bg-white p-2 rounded border border-blue-200 text-right">
                                        <span className="block text-xs text-gray-500 uppercase font-bold">Final Price</span>
                                        <span className="text-lg font-bold text-green-600 flex items-center justify-end gap-2">
                                            <FaCalculator className="text-gray-400 text-xs" />
                                            {helpers.formatCurrency(calculateFinalPrice(), 'NGN')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {/* ---------------------------------- */}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Stock Quantity *</label>
                                    <input type="number" name="stock_quantity" value={formData.stock_quantity} onChange={handleInputChange} required className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 flex items-center gap-1"><FaWeightHanging /> Weight (kg)</label>
                                    <input type="number" step="0.1" name="weight_kg" value={formData.weight_kg} onChange={handleInputChange} placeholder="e.g. 0.5" className="p-2 border rounded w-full" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* CATEGORY */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Category</label>
                                    <select 
                                        name="category_id" 
                                        value={isCustomCategory ? 'other' : formData.category_id} 
                                        onChange={handleInputChange} 
                                        className="p-2 border rounded w-full bg-white"
                                    >
                                        {Object.keys(CATEGORY_GROUPS).map(group => (
                                            <optgroup key={group} label={group}>
                                                {CATEGORY_GROUPS[group].map(cat => (
                                                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                                                ))}
                                            </optgroup>
                                        ))}
                                        <option value="other" className="font-bold text-blue-600">Others (Specify)</option>
                                    </select>
                                    {isCustomCategory && (
                                        <input 
                                            type="text" name="custom_category" 
                                            value={formData.custom_category} onChange={handleInputChange}
                                            placeholder="Enter custom category"
                                            className="mt-2 p-2 border border-blue-300 rounded w-full focus:ring-2 focus:ring-blue-500"
                                        />
                                    )}
                                </div>

                                {/* BRAND */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Brand</label>
                                    <select 
                                        name="brand_id" 
                                        value={isCustomBrand ? 'other' : formData.brand_id} 
                                        onChange={handleInputChange} 
                                        className="p-2 border rounded w-full bg-white"
                                    >
                                        {PART_BRANDS.map(b => (
                                            <option key={b.id} value={b.id}>{b.label}</option>
                                        ))}
                                        <option value="other" className="font-bold text-blue-600">Others (Specify)</option>
                                    </select>
                                    {isCustomBrand && (
                                        <input 
                                            type="text" name="custom_brand" 
                                            value={formData.custom_brand} onChange={handleInputChange}
                                            placeholder="Enter custom brand"
                                            className="mt-2 p-2 border border-blue-300 rounded w-full focus:ring-2 focus:ring-blue-500"
                                        />
                                    )}
                                </div>

                                {/* CONDITION */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Condition</label>
                                    <select 
                                        name="condition" 
                                        value={isCustomCondition ? 'other' : formData.condition} 
                                        onChange={handleInputChange} 
                                        className="p-2 border rounded w-full bg-white"
                                    >
                                        {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                                        <option value="other" className="font-bold text-blue-600">Others (Specify)</option>
                                    </select>
                                    {isCustomCondition && (
                                        <input 
                                            type="text" name="custom_condition" 
                                            value={formData.custom_condition} onChange={handleInputChange}
                                            placeholder="Enter custom condition"
                                            className="mt-2 p-2 border border-blue-300 rounded w-full focus:ring-2 focus:ring-blue-500"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Vehicle Fitment Fields */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Make *</label>
                                    <input name="make" value={formData.make} onChange={handleInputChange} required className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Model *</label>
                                    <input name="model" value={formData.model} onChange={handleInputChange} required className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Year Range</label>
                                    <input name="year_range" value={formData.year_range} onChange={handleInputChange} placeholder="e.g. 2010-2015" className="p-2 border rounded w-full" />
                                </div>
                            </div>

                            {/* Delivery SLA */}
                            <div className="bg-green-50 p-4 rounded border border-green-100">
                                <label className="block text-sm font-bold text-green-800 mb-1 flex items-center gap-2">
                                    <FaShippingFast /> Expected Delivery Time (SLA)
                                </label>
                                <select 
                                    name="expected_delivery_date" 
                                    value={formData.expected_delivery_date} 
                                    onChange={handleInputChange} 
                                    className="p-2 border rounded w-full bg-white"
                                >
                                    {DELIVERY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* OEM Toggle */}
                            <div className="flex items-center gap-2 bg-blue-50 p-3 rounded border border-blue-100">
                                <input 
                                    type="checkbox" 
                                    name="is_oem" 
                                    checked={formData.is_oem} 
                                    onChange={handleInputChange} 
                                    className="h-5 w-5 text-primary rounded" 
                                />
                                <div>
                                    <span className="text-sm font-bold text-blue-800">Genuine OEM Part?</span>
                                    <p className="text-xs text-blue-600">Check this if the part is an original manufacturer part (e.g. Toyota Box), not aftermarket.</p>
                                </div>
                            </div>

                            {/* Description with Magic Wand */}
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-xs font-bold text-gray-600">Description</label>
                                    <button
                                        type="button"
                                        onClick={handleGenerateDescription}
                                        disabled={isGenerating}
                                        className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-bold flex items-center gap-1 hover:bg-purple-200 disabled:opacity-50"
                                    >
                                        {isGenerating ? <FaSpinner className="animate-spin" /> : <FaMagic />} AI Generate
                                    </button>
                                </div>
                                <textarea name="description" value={formData.description} onChange={handleInputChange} className="p-2 border rounded w-full h-24"></textarea>
                            </div>
                            
                            {/* Images */}
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-2">Images (Primary is required)</label>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {IMAGE_SLOTS.map(slot => (
                                        <div key={slot.key} className="relative border-2 border-dashed rounded h-24 flex flex-col items-center justify-center text-center p-1 hover:bg-gray-50 transition-colors group">
                                            
                                            {uploadingSlot === slot.key ? (
                                                <div className="flex flex-col items-center text-primary">
                                                    <FaSpinner className="animate-spin text-xl mb-1" />
                                                    <span className="text-[10px]">Uploading...</span>
                                                </div>
                                            ) : formData.images[slot.key] ? (
                                                <>
                                                    <img src={formData.images[slot.key]} alt={slot.label} className="w-full h-full object-cover rounded" />
                                                    <button 
                                                        type="button"
                                                        onClick={() => setFormData(prev => ({ ...prev, images: { ...prev.images, [slot.key]: null } }))}
                                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <FaTrash size={10} />
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-center gap-2 w-full px-2">
                                                    <button 
                                                        type="button"
                                                        onClick={() => handleCameraClick(slot.key)}
                                                        className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow-md transition-transform hover:scale-110"
                                                        title="Snap & Save"
                                                    >
                                                        <FaCamera />
                                                    </button>
                                                    
                                                    <span className="text-xs text-gray-400">- OR -</span>

                                                    <label className="cursor-pointer text-gray-500 hover:text-primary flex flex-col items-center">
                                                        <FaFolderOpen className="text-xl" />
                                                        <span className="text-[10px] mt-1">Browse</span>
                                                        <input 
                                                            type="file" 
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={(e) => handleFileInput(e, slot.key)}
                                                        />
                                                    </label>
                                                </div>
                                            )}
                                            <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-[9px] py-1 px-1 truncate">
                                                {slot.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-gray-600">Cancel</button>
                                <button type="submit" disabled={saving || uploading || isGenerating} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark">
                                    {saving ? 'Saving...' : 'Save Part'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* CAMERA MODAL */}
            <CameraModal 
                isOpen={isCameraOpen} 
                onClose={() => setIsCameraOpen(false)} 
                onCapture={handleCameraCapture}
                viewType={activeViewType || 'image'}
            />
        </div>
    );
}

export default AdminInventory;
